<script setup lang="ts">
/**
 * /tailor-resume-to-job body: why tailoring matters, how the editor's
 * "Tailor to a job" section works, what it never does, and what is free.
 * The buttons go back up to the tool at the top of the page
 * (MarketingTailorTool), where the visitor's CV is tailored there and then.
 */
const { t } = useI18n()

function toTool() {
  document.getElementById('tailor-tool')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const steps = [
  { id: 'paste', icon: 'bi-clipboard-check' },
  { id: 'match', icon: 'bi-bullseye' },
  { id: 'pick', icon: 'bi-ui-checks' },
  { id: 'save', icon: 'bi-files' },
] as const

const promises = ['invent', 'skills', 'numbers', 'accept'] as const
const freeItems = ['score', 'matching', 'missing'] as const
const premiumItems = ['rewrites', 'apply', 'copies', 'unlimited'] as const
</script>

<template>
  <!-- Why -->
  <section class="sofax surface-alt relative overflow-hidden">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <div class="eyebrow mb-5">{{ t('tailorPage.why.eyebrow') }}</div>
            <h2 class="mb-5 !text-3xl md:!text-[2.5rem]">{{ t('tailorPage.why.title') }}</h2>
            <p class="mb-5 text-lg">{{ t('tailorPage.why.p1') }}</p>
            <p class="mb-8">{{ t('tailorPage.why.p2') }}</p>
            <div class="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
              <SofaxBtn :label="t('tailorPage.cta')" variant="accent" @click="toTool" />
              <SofaxArrowLink to="/ats-check" :label="t('tailorPage.atsLink')" />
            </div>
          </div>
          <div class="mx-auto w-full max-w-[31.25rem]">
            <MarketingTailorMockCard />
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- How it works -->
  <section class="sofax bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell">
        <SofaxSectionHeading
          :eyebrow="t('tailorPage.how.eyebrow')"
          :title="t('tailorPage.how.title')"
          :text="t('tailorPage.how.subtitle')"
        />
        <ol class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <li
            v-for="(step, index) in steps"
            :key="step.id"
            class="h-full rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800"
          >
            <div class="flex items-center gap-4">
              <SofaxIconTile :icon="step.icon" size="sm" />
              <span class="font-title text-sm font-bold text-muted dark:text-gray-400">{{ index + 1 }} / {{ steps.length }}</span>
            </div>
            <div class="display-heading display-heading-5 mb-3 mt-6">{{ t(`tailorPage.how.steps.${step.id}.title`) }}</div>
            <p class="text-base">{{ t(`tailorPage.how.steps.${step.id}.text`) }}</p>
          </li>
        </ol>
      </div>
    </div>
  </section>

  <!-- Honesty + free vs premium -->
  <section class="sofax surface-alt">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800 md:p-10">
            <SofaxIconTile icon="bi-shield-check" round size="md" />
            <h2 class="mb-6 mt-8 !text-2xl !text-white md:!text-3xl">{{ t('tailorPage.honest.title') }}</h2>
            <ul class="flex flex-col gap-4">
              <li v-for="item in promises" :key="item" class="flex items-start gap-3">
                <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent-soft" />
                <span class="text-base !text-white/85">{{ t(`tailorPage.honest.items.${item}`) }}</span>
              </li>
            </ul>
          </div>

          <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-2">{{ t('tailorPage.plans.free.title') }}</div>
              <p class="mb-5 text-base text-muted dark:text-gray-400">{{ t('tailorPage.plans.free.text') }}</p>
              <ul class="flex flex-col gap-3">
                <li v-for="item in freeItems" :key="item" class="flex items-start gap-3">
                  <i class="bi bi-check2 mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`tailorPage.plans.free.items.${item}`) }}</span>
                </li>
              </ul>
            </div>
            <div class="rounded-[0.625rem] border-2 border-accent bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-2">{{ t('tailorPage.plans.premium.title') }}</div>
              <p class="mb-5 text-base text-muted dark:text-gray-400">{{ t('tailorPage.plans.premium.text') }}</p>
              <ul class="flex flex-col gap-3">
                <li v-for="item in premiumItems" :key="item" class="flex items-start gap-3">
                  <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`tailorPage.plans.premium.items.${item}`) }}</span>
                </li>
              </ul>
              <div class="mt-6">
                <SofaxArrowLink to="/pricing" :label="t('tailorPage.plans.pricingLink')" />
              </div>
            </div>
          </div>
        </div>

        <div class="mt-14 text-center">
          <SofaxBtn :label="t('tailorPage.cta')" variant="dark" @click="toTool" />
          <p class="mt-4 text-sm text-muted dark:text-gray-400">{{ t('tailorPage.ctaNote') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>
